import { RouteFocus } from "@clones/ui";
import { useRouterState } from "@tanstack/react-router";
import { ErrorState, Spinner } from "@clones/ui";
import { createRootRoute, createRoute, createRouter, Outlet, lazyRouteComponent } from "@tanstack/react-router";
import { RespondentAppShell } from "@/components/app-shell";
import { WebInterviewGate } from "@/runtime/feature-gate";

import { NotFoundPage } from "@/pages/not-found";

/**
 * Flat route tree. The whole app sits behind `WebInterviewGate` (runtime
 * feature flag); authenticated routes additionally wrap in
 * `RespondentAppShell` (which redirects to /login when the cookie session is
 * missing). Public auth pages skip the shell but still honour the gate so a
 * gated-off deployment shows a single consistent "unavailable" screen.
 */

const LoginPage = lazyRouteComponent(() => import("@/pages/login"), "LoginPage");
const RegisterPage = lazyRouteComponent(() => import("@/pages/register"), "RegisterPage");
const VerifyPage = lazyRouteComponent(() => import("@/pages/verify"), "VerifyPage");
const MfaPage = lazyRouteComponent(() => import("@/pages/mfa"), "MfaPage");
const LandingPage = lazyRouteComponent(() => import("@/pages/landing"), "LandingPage");
const InterviewRunner = lazyRouteComponent(() => import("@/pages/interview/runner"), "InterviewRunner");
const MiniAppPage = lazyRouteComponent(() => import("@/pages/miniapp"), "MiniAppPage");
const SettingsPage = lazyRouteComponent(() => import("@/pages/settings"), "SettingsPage");

function RootContent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const settled = useRouterState({ select: (state) => !state.isLoading && state.status === "idle" });
  return <><RouteFocus pathname={pathname} settled={settled} /><Outlet /></>;
}

const rootRoute = createRootRoute({
  component: RootContent,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <WebInterviewGate>
      <LoginPage />
    </WebInterviewGate>
  ),
  // Optional keys so existing navigate({ to: "/login" }) callers needn't pass
  // every param. `verified` is set only by the backend verify-link redirect
  // (/login?verified=1|0) — surfaced as a banner (tester: the link landed on a
  // wrong, auth-gated page).
  validateSearch: (search: Record<string, unknown>): { redirect?: string; verified?: string } => ({
    redirect: typeof search["redirect"] === "string" ? search["redirect"] : undefined,
    // TanStack's search parser coerces ?verified=1 to the NUMBER 1, so a
    // typeof === "string" check would drop it (redirect, a path, stays a
    // string and is unaffected). Coerce to string so the /login banner's
    // `=== "1"` / `=== "0"` comparison matches the backend redirect.
    verified:
      search["verified"] === undefined || search["verified"] === null
        ? undefined
        : String(search["verified"]),
  }),
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: () => (
    <WebInterviewGate>
      <RegisterPage />
    </WebInterviewGate>
  ),
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify",
  component: () => (
    <WebInterviewGate>
      <VerifyPage />
    </WebInterviewGate>
  ),
});

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mfa",
  component: () => (
    <WebInterviewGate>
      <MfaPage />
    </WebInterviewGate>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <WebInterviewGate>
      <RespondentAppShell>
        <LandingPage />
      </RespondentAppShell>
    </WebInterviewGate>
  ),
});

const interviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/interview",
  component: () => (
    <WebInterviewGate>
      <RespondentAppShell>
        <InterviewRunner />
      </RespondentAppShell>
    </WebInterviewGate>
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => (
    <WebInterviewGate>
      <RespondentAppShell>
        <SettingsPage />
      </RespondentAppShell>
    </WebInterviewGate>
  ),
});

const miniAppRoute = createRoute({ getParentRoute: () => rootRoute, path: "/miniapp", component: MiniAppPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  verifyRoute,
  mfaRoute,
  indexRoute,
  interviewRoute,
  settingsRoute,
  miniAppRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPendingMs: 200,
  defaultPendingComponent: () => <div className="grid min-h-[40vh] place-items-center"><Spinner size="lg" /></div>,
  defaultErrorComponent: () => <ErrorState onRetry={() => window.location.reload()} />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
